import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../components/Layout';
import { Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { controllingAssignmentsAPI, controllingAPI, companyAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const ControllingAssignments = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('controlling_assignments');
  const [activeTab, setActiveTab] = useState('ca-cc'); // 'ca-cc', 'pc-cc', 'cc-pc'
  
  // Master data
  const [controllingAreas, setControllingAreas] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [costCenters, setCostCenters] = useState([]);

  // Assignments data
  const [caCompanyCodeAssignments, setCaCompanyCodeAssignments] = useState([]);
  const [pcCompanyCodeAssignments, setPcCompanyCodeAssignments] = useState([]);
  const [ccProfitCenterAssignments, setCcProfitCenterAssignments] = useState([]);
  
  // Form selections for CA-CC tab
  const [newCaControllingArea, setNewCaControllingArea] = useState('');
  const [newCaCompanyCode, setNewCaCompanyCode] = useState('');
  
  // Form selections for PC-CC tab
  const [newPcProfitCenter, setNewPcProfitCenter] = useState('');
  const [newPcCompanyCode, setNewPcCompanyCode] = useState('');
  const [newPcControllingArea, setNewPcControllingArea] = useState('');
  
  // Form selections for CC-PC tab
  const [newCcCostCenter, setNewCcCostCenter] = useState('');
  const [newCcProfitCenter, setNewCcProfitCenter] = useState('');
  const [newCcControllingArea, setNewCcControllingArea] = useState('');

  useEffect(() => {
    loadMasterData();
  }, []);


  const loadMasterData = async () => {
    try {
      const [caResponse, ccResponse, pcResponse, costCenterResponse] = await Promise.all([
        controllingAPI.getControllingAreas(),
        companyAPI.getCompanyCodes(),
        controllingAPI.getProfitCenters(),
        controllingAPI.getCostCenters(),
      ]);
      setControllingAreas(caResponse.data);
      setCompanyCodes(ccResponse.data);
      setProfitCenters(pcResponse.data);
      setCostCenters(costCenterResponse.data);
    } catch (error) {
      toast.error('Failed to load master data');
    }
  };

  const loadAssignments = useCallback(async () => {
    try {
      if (activeTab === 'ca-cc') {
        const response = await controllingAssignmentsAPI.getControllingAreaCompanyCodeAssignments('', '');
        setCaCompanyCodeAssignments(response.data);
      } else if (activeTab === 'pc-cc') {
        const response = await controllingAssignmentsAPI.getProfitCenterCompanyCodeAssignments('', '');
        setPcCompanyCodeAssignments(response.data);
      } else if (activeTab === 'cc-pc') {
        const response = await controllingAssignmentsAPI.getCostCenterProfitCenterAssignments('', '');
        setCcProfitCenterAssignments(response.data);
      }
    } catch (error) {
      toast.error('Failed to load assignments');
    }
  }, [activeTab]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  // Handlers for CA-CC assignments
  const handleAddCaCompanyCodeAssignment = async () => {
    if (!newCaControllingArea || !newCaCompanyCode) {
      toast.error('Please select both Controlling Area and Company Code');
      return;
    }

    try {
      await controllingAssignmentsAPI.createControllingAreaCompanyCodeAssignment({
        controlling_area_id: newCaControllingArea,
        company_code_id: newCaCompanyCode
      });
      
      setNewCaControllingArea('');
      setNewCaCompanyCode('');
      loadAssignments();
      toast.success('Assignment created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleDeleteCaCompanyCodeAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await controllingAssignmentsAPI.deleteControllingAreaCompanyCodeAssignment(assignmentId);
      loadAssignments();
      toast.success('Assignment deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assignment');
    }
  };

  // Handlers for PC-CC assignments
  const handleAddPcCompanyCodeAssignment = async () => {
    if (!newPcProfitCenter || !newPcCompanyCode || !newPcControllingArea) {
      toast.error('Please select all required fields');
      return;
    }

    try {
      await controllingAssignmentsAPI.createProfitCenterCompanyCodeAssignment({
        profit_center_id: newPcProfitCenter,
        company_code_id: newPcCompanyCode,
        controlling_area_id: newPcControllingArea
      });
      
      setNewPcProfitCenter('');
      setNewPcCompanyCode('');
      setNewPcControllingArea('');
      loadAssignments();
      toast.success('Assignment created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleDeletePcCompanyCodeAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await controllingAssignmentsAPI.deleteProfitCenterCompanyCodeAssignment(assignmentId);
      loadAssignments();
      toast.success('Assignment deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assignment');
    }
  };

  // Handlers for CC-PC assignments
  const handleAddCcProfitCenterAssignment = async () => {
    if (!newCcCostCenter || !newCcProfitCenter || !newCcControllingArea) {
      toast.error('Please select all required fields');
      return;
    }

    try {
      await controllingAssignmentsAPI.createCostCenterProfitCenterAssignment({
        cost_center_id: newCcCostCenter,
        profit_center_id: newCcProfitCenter,
        controlling_area_id: newCcControllingArea
      });
      
      setNewCcCostCenter('');
      setNewCcProfitCenter('');
      setNewCcControllingArea('');
      loadAssignments();
      toast.success('Assignment created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleDeleteCcProfitCenterAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await controllingAssignmentsAPI.deleteCostCenterProfitCenterAssignment(assignmentId);
      loadAssignments();
      toast.success('Assignment deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assignment');
    }
  };

  const tabs = [
    { id: 'ca-cc', label: 'Controlling Area → Company Code', ratio: '1:N' },
    { id: 'pc-cc', label: 'Profit Center → Company Code', ratio: 'M:1' },
    { id: 'cc-pc', label: 'Cost Center → Profit Center', ratio: '1:1' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'ca-cc':
        return (
          <>
            {/* Info Card */}
            <Card className="p-6 mb-6 bg-orange-50 border-orange-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">SAP Business Rule (1:N)</h3>
              <p className="text-sm text-gray-700">
                All Company Codes assigned to a Controlling Area must share the same <strong>Chart of Accounts</strong> and <strong>Fiscal Year Variant</strong>.
                One Controlling Area can have multiple Company Codes.
              </p>
            </Card>

            {/* Add Assignment Form */}
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Assignment</h3>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Controlling Area</label>
                  <Select value={newCaControllingArea} onValueChange={setNewCaControllingArea}>
                    <SelectTrigger><SelectValue placeholder="Select Controlling Area" /></SelectTrigger>
                    <SelectContent>
                      {controllingAreas.map(ca => (
                        <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                          {ca.controlling_area_id} - {ca.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Code</label>
                  <Select value={newCaCompanyCode} onValueChange={setNewCaCompanyCode}>
                    <SelectTrigger><SelectValue placeholder="Select Company Code" /></SelectTrigger>
                    <SelectContent>
                      {companyCodes.map(cc => (
                        <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                          {cc.company_code_id} - {cc.company_code_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {writable && (<Button onClick={handleAddCaCompanyCodeAssignment}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>)}
              </div>
            </Card>

            {/* Assignments Table */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Assignments</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Controlling Area</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Company Code</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caCompanyCodeAssignments.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center py-8 text-gray-500">No assignments found</td>
                      </tr>
                    ) : (
                      caCompanyCodeAssignments.map((assignment) => (
                        <tr key={assignment.assignment_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-mono text-sm">{assignment.controlling_area_id}</td>
                          <td className="py-3 px-4 font-mono text-sm">{assignment.company_code_id}</td>
                          <td className="py-3 px-4 text-right">
                            {writable && (<Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteCaCompanyCodeAssignment(assignment.assignment_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        );

      case 'pc-cc':
        return (
          <>
            {/* Info Card */}
            <Card className="p-6 mb-6 bg-green-50 border-green-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">SAP Business Rule (M:1)</h3>
              <p className="text-sm text-gray-700">
                <strong>PC posts go to legal entity.</strong> Multiple Profit Centers can be assigned to one Company Code.
                This defines where profit center transactions are legally recorded.
              </p>
            </Card>

            {/* Add Assignment Form */}
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Assignment</h3>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Controlling Area</label>
                  <Select value={newPcControllingArea} onValueChange={setNewPcControllingArea}>
                    <SelectTrigger><SelectValue placeholder="Select Controlling Area" /></SelectTrigger>
                    <SelectContent>
                      {controllingAreas.map(ca => (
                        <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                          {ca.controlling_area_id} - {ca.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Profit Center</label>
                  <Select value={newPcProfitCenter} onValueChange={setNewPcProfitCenter}>
                    <SelectTrigger><SelectValue placeholder="Select Profit Center" /></SelectTrigger>
                    <SelectContent>
                      {profitCenters.filter(pc => !newPcControllingArea || pc.controlling_area_id === newPcControllingArea).map(pc => (
                        <SelectItem key={pc.profit_center_id} value={pc.profit_center_id}>
                          {pc.profit_center_id} - {pc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Code</label>
                  <Select value={newPcCompanyCode} onValueChange={setNewPcCompanyCode}>
                    <SelectTrigger><SelectValue placeholder="Select Company Code" /></SelectTrigger>
                    <SelectContent>
                      {companyCodes.map(cc => (
                        <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                          {cc.company_code_id} - {cc.company_code_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {writable && (<Button onClick={handleAddPcCompanyCodeAssignment}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>)}
              </div>
            </Card>

            {/* Assignments Table */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Assignments</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Profit Center</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Company Code</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Controlling Area</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pcCompanyCodeAssignments.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-8 text-gray-500">No assignments found</td>
                      </tr>
                    ) : (
                      pcCompanyCodeAssignments.map((assignment) => (
                        <tr key={assignment.assignment_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-mono text-sm">{assignment.profit_center_id}</td>
                          <td className="py-3 px-4 font-mono text-sm">{assignment.company_code_id}</td>
                          <td className="py-3 px-4 font-mono text-xs text-gray-600">{assignment.controlling_area_id}</td>
                          <td className="py-3 px-4 text-right">
                            {writable && (<Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeletePcCompanyCodeAssignment(assignment.assignment_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        );

      case 'cc-pc':
        return (
          <>
            {/* Info Card */}
            <Card className="p-6 mb-6 bg-orange-50 border-orange-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">SAP Business Rule (1:1)</h3>
              <p className="text-sm text-gray-700">
                <strong>Mandatory in S/4; best practice in ECC.</strong> Each Cost Center must be assigned to exactly one Profit Center.
                This enables profitability analysis by linking cost accounting to profit center accounting.
              </p>
            </Card>

            {/* Add Assignment Form */}
            <Card className="p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Assignment</h3>
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Controlling Area</label>
                  <Select value={newCcControllingArea} onValueChange={setNewCcControllingArea}>
                    <SelectTrigger><SelectValue placeholder="Select Controlling Area" /></SelectTrigger>
                    <SelectContent>
                      {controllingAreas.map(ca => (
                        <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                          {ca.controlling_area_id} - {ca.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cost Center</label>
                  <Select value={newCcCostCenter} onValueChange={setNewCcCostCenter}>
                    <SelectTrigger><SelectValue placeholder="Select Cost Center" /></SelectTrigger>
                    <SelectContent>
                      {costCenters.filter(cc => !newCcControllingArea || cc.controlling_area_id === newCcControllingArea).map(cc => (
                        <SelectItem key={cc.cost_center_id} value={cc.cost_center_id}>
                          {cc.cost_center_id} - {cc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Profit Center</label>
                  <Select value={newCcProfitCenter} onValueChange={setNewCcProfitCenter}>
                    <SelectTrigger><SelectValue placeholder="Select Profit Center" /></SelectTrigger>
                    <SelectContent>
                      {profitCenters.filter(pc => !newCcControllingArea || pc.controlling_area_id === newCcControllingArea).map(pc => (
                        <SelectItem key={pc.profit_center_id} value={pc.profit_center_id}>
                          {pc.profit_center_id} - {pc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {writable && (<Button onClick={handleAddCcProfitCenterAssignment}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>)}
              </div>
            </Card>

            {/* Assignments Table */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Assignments</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Cost Center</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Profit Center</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Controlling Area</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ccProfitCenterAssignments.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-8 text-gray-500">No assignments found</td>
                      </tr>
                    ) : (
                      ccProfitCenterAssignments.map((assignment) => (
                        <tr key={assignment.assignment_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-mono text-sm">{assignment.cost_center_id}</td>
                          <td className="py-3 px-4 font-mono text-sm">{assignment.profit_center_id}</td>
                          <td className="py-3 px-4 font-mono text-xs text-gray-600">{assignment.controlling_area_id}</td>
                          <td className="py-3 px-4 text-right">
                            {writable && (<Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteCcProfitCenterAssignment(assignment.assignment_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Controlling Assignments</h2>
          <p className="text-sm text-gray-500 mt-1">Manage key relationships in Controlling</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                }}
                className={`px-6 py-4 font-medium whitespace-nowrap flex-1 ${
                  activeTab === tab.id
                    ? 'text-orange-600 border-b-2 border-orange-600'
                    : 'text-gray-600'
                }`}
              >
                <div>{tab.label}</div>
                <div className="text-xs mt-1 font-normal">
                  <span className={`px-2 py-1 rounded ${
                    activeTab === tab.id ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.ratio}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </Layout>
  );
};

export default ControllingAssignments;
